import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withModifiers as _withModifiers, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["disabled", "title"]
const _hoisted_2 = ["title"]
const _hoisted_3 = { class: "main-content" }
const _hoisted_4 = { class: "video-wrapper" }
const _hoisted_5 = ["src", "poster"]
const _hoisted_6 = ["src"]
const _hoisted_7 = {
  key: 1,
  class: "video-status",
  "aria-hidden": "true"
}
const _hoisted_8 = { class: "play-section" }
const _hoisted_9 = { class: "progress-indicator" }
const _hoisted_10 = { class: "progress-text" }
const _hoisted_11 = {
  class: "swipe-area",
  ref: "swipeArea"
}
const _hoisted_12 = { class: "settings-list" }
const _hoisted_13 = { class: "settings-item" }
const _hoisted_14 = { class: "switch" }
const _hoisted_15 = { class: "settings-item" }
const _hoisted_16 = { class: "switch" }

import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getTprList, type TprVO } from '@/api/course-tpr'
import { addHistory } from '@/api/history'
import { getFavorites, addFavorites, deleteFavorites } from '@/api/favorites'
import { updateLearning } from '@/api/user'
import { getAccessToken } from '@/config/axios/auth'

const DEFAULT_TPR_CATEGORY = '身体指令'
const TPR_CONTENT_TYPE = 3


export default /*@__PURE__*/_defineComponent({
  __name: 'tpr-fullscreen',
  setup(__props) {

const isGuestUser = computed(() => getAccessToken() == null)
const isNonVipUser = computed(() =>
  !isGuestUser.value && localStorage.getItem('vipTag') === 'false'
)

const route = useRoute()
const router = useRouter()
const tprList = ref<TprVO[]>([])
const isLoading = ref(false)
const historyRecordedIds = new Set<number>()

const currentIndex = ref(0)
const isCleanMode = ref(false)
const isPlaying = ref(false)
const isVideoReady = ref(false)
const isAutoMode = ref(false)
const autoPlayTimer = ref(null)
const showSettings = ref(false)
const showTranslation = ref(true)
const manualFlipAutoPlay = ref(true)
const favoriteCardIds = ref([])
const favoriteLoadingIds = ref([])
const fullscreenVideoRef = ref(null)
const videoAspectRatios = ref<Record<string, string>>({})
let learningUpdateTimer: ReturnType<typeof window.setInterval> | null = null
let isLearningUpdatePending = false

const stopLearningUpdateTimer = () => {
  if (learningUpdateTimer != null) {
    window.clearInterval(learningUpdateTimer)
    learningUpdateTimer = null
  }
}

const sendLearningUpdate = async () => {
  if (isGuestUser.value || isLearningUpdatePending) {
    return
  }

  isLearningUpdatePending = true
  try {
    await updateLearning()
  } catch {
    // Learning progress updates should not interrupt playback.
  } finally {
    isLearningUpdatePending = false
  }
}

const startLearningUpdateTimer = () => {
  stopLearningUpdateTimer()

  if (isGuestUser.value) {
    return
  }

  learningUpdateTimer = window.setInterval(sendLearningUpdate, 60 * 1000)
}

const queryParams = ref({
  category: route.query.category || DEFAULT_TPR_CATEGORY,
  stage: route.query.stage
})

const cardColors = [
  'rgba(255, 140, 66, 0.15)',
  'rgba(91, 143, 249, 0.15)',
  'rgba(76, 175, 80, 0.15)',
  'rgba(231, 76, 60, 0.15)',
  'rgba(255, 193, 7, 0.15)',
  'rgba(156, 39, 176, 0.15)',
  'rgba(0, 150, 136, 0.15)',
  'rgba(33, 150, 243, 0.15)'
]

const cards = computed(() => {
  return tprList.value.map((item, index) => ({
    id: item.id,
    en: item.english,
    zh: item.chinese,
    videoUrl: item.videoUrl,
    coverUrl: item.coverUrl,
    bgColor: cardColors[index % cardColors.length]
  }))
})

const emptyCard = {
  id: 0,
  en: '',
  zh: '',
  videoUrl: '',
  coverUrl: '',
  bgColor: 'rgba(255, 255, 255, 0.5)'
}

// 当前卡片
const currentCard = computed(() => cards.value[currentIndex.value] || emptyCard)
const currentVideoAspectRatio = computed(() => {
  return videoAspectRatios.value[String(currentCard.value.id)] || '4 / 5'
})
const isCurrentCardFavorite = computed(() => favoriteCardIds.value.includes(currentCard.value.id))
const isCurrentFavoriteLoading = computed(() => favoriteLoadingIds.value.includes(currentCard.value.id))

const setFavoriteState = (cardId, isFavorite) => {
  const idSet = new Set(favoriteCardIds.value)

  if (isFavorite) {
    idSet.add(cardId)
  } else {
    idSet.delete(cardId)
  }

  favoriteCardIds.value = Array.from(idSet)
}

const loadFavoriteState = async (cardId) => {
  if (!cardId || favoriteLoadingIds.value.includes(cardId)) {
    return
  }

  favoriteLoadingIds.value = [...favoriteLoadingIds.value, cardId]
  try {
    const favorite = await getFavorites(TPR_CONTENT_TYPE, cardId)
    setFavoriteState(cardId, favorite != null)
  } finally {
    favoriteLoadingIds.value = favoriteLoadingIds.value.filter((id) => id !== cardId)
  }
}

const toggleFavorite = async () => {
  if(isGuestUser.value) {
    openLoginPrompt()
    return
  }

  const cardId = currentCard.value.id
  if (!cardId || favoriteLoadingIds.value.includes(cardId)) {
    return
  }

  const wasFavorite = favoriteCardIds.value.includes(cardId)
  favoriteLoadingIds.value = [...favoriteLoadingIds.value, cardId]
  setFavoriteState(cardId, !wasFavorite)

  try {
    if (wasFavorite) {
      await deleteFavorites(TPR_CONTENT_TYPE, cardId)
    } else {
      await addFavorites({
        contentType: TPR_CONTENT_TYPE,
        contentId: cardId
      })
    }
  } catch {
    setFavoriteState(cardId, wasFavorite)
  } finally {
    favoriteLoadingIds.value = favoriteLoadingIds.value.filter((id) => id !== cardId)
  }
}

const openLoginPrompt = () => {
  router.replace('/login')
}

const ensureCanUseTprFullscreen = () => {
  if (isGuestUser.value) {
    router.replace('/login')
    return false
  }

  if (isNonVipUser.value) {
    router.replace('/vip')
    return false
  }

  return true
}

const playVideo = async (restart = true) => {
  if (!ensureCanUseTprFullscreen()) {
    return
  }

  const video = fullscreenVideoRef.value
  if (!video) return

  if (restart) {
    video.currentTime = 0
  }

  try {
    await video.play()
  } catch (error) {
    isPlaying.value = false
  }
}

// 切换清屏模式
const toggleCleanMode = () => {
  isCleanMode.value = !isCleanMode.value
}

// 切换播放/暂停
const togglePlay = () => {
  if (isPlaying.value) {
    stopPlay()
  } else {
    void startPlay()
  }
}

// 开始播放
const startPlay = async (restart = true) => {
  if (!ensureCanUseTprFullscreen()) {
    return
  }

  if (!currentCard.value.videoUrl) {
    return
  }

  await nextTick()
  await playVideo(restart)
  
  if (isAutoMode.value && !autoPlayTimer.value) {
    autoPlayTimer.value = setInterval(() => {
      nextCard(false)
    }, 3000)
  }
}

// 停止播放
const stopPlay = () => {
  isPlaying.value = false
  const video = fullscreenVideoRef.value
  if (video) {
    video.pause()
    video.currentTime = 0
  }

  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value)
    autoPlayTimer.value = null
  }
}

// 开始自动播放
const startAutoPlay = () => {
  if (!ensureCanUseTprFullscreen()) {
    return
  }

  if (cards.value.length === 0) {
    return
  }

  if (isAutoMode.value) {
    isAutoMode.value = false
    stopPlay()
    return
  }

  isAutoMode.value = true
  void startPlay()
}



// 打开设置
const openSettings = () => {
  showSettings.value = true
}

const closeSettings = () => {
  showSettings.value = false
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/card')
}

// 上一张
const prevCard = (isManual = true) => {
  if (cards.value.length === 0) {
    return
  }

  stopPlay()
  currentIndex.value = (currentIndex.value - 1 + cards.value.length) % cards.value.length
  if (!isManual || manualFlipAutoPlay.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
  if (isAutoMode.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
}

// 下一张
const nextCard = (isManual = true) => {
  if (cards.value.length === 0) {
    return
  }

  stopPlay()
  currentIndex.value = (currentIndex.value + 1) % cards.value.length
  if (!isManual || manualFlipAutoPlay.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
  if (isAutoMode.value) {
    setTimeout(() => {
      void startPlay()
    }, 100)
  }
}

// 触摸滑动
let touchStartX = 0
let touchEndX = 0

const handleTouchStart = (e) => {
  touchStartX = e.changedTouches[0].screenX
}

const handleTouchEnd = (e) => {
  touchEndX = e.changedTouches[0].screenX
  handleSwipe()
}

const handleSwipe = () => {
  const swipeThreshold = 50
  const diff = touchStartX - touchEndX
  
  if (Math.abs(diff) > swipeThreshold) {
    if (diff > 0) {
      // 向左滑，下一张
      nextCard()
    } else {
      // 向右滑，上一张
      prevCard()
    }
  }
}

// 键盘事件
const handleKeydown = (e) => {
  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    prevCard()
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    nextCard()
  } else if (e.key === ' ') {
    e.preventDefault()
    togglePlay()
  } else if (e.key === 'Escape') {
    router.push('/category')
  }
}

const handleVideoPlay = () => {
  if (!ensureCanUseTprFullscreen()) {
    const video = fullscreenVideoRef.value
    if (video) {
      video.pause()
      video.currentTime = 0
    }
    return
  }

  isPlaying.value = true
  if(!isGuestUser.value) {
    addPlayHistory(currentCard.value.id)
  }
}

const setCardAspectRatio = (cardId, width, height) => {
  if (!cardId || !width || !height) {
    return
  }

  videoAspectRatios.value = {
    ...videoAspectRatios.value,
    [String(cardId)]: `${width} / ${height}`
  }
}

const preloadCoverAspectRatio = (card) => {
  if (!card?.id || !card.coverUrl || videoAspectRatios.value[String(card.id)]) {
    return
  }

  const image = new Image()
  image.onload = () => {
    setCardAspectRatio(card.id, image.naturalWidth, image.naturalHeight)
  }
  image.src = card.coverUrl
}

const handleVideoMetadata = (event: Event) => {
  const video = event.target as HTMLVideoElement | null
  const cardId = currentCard.value.id
  if (!video?.videoWidth || !video?.videoHeight) {
    return
  }

  setCardAspectRatio(cardId, video.videoWidth, video.videoHeight)
}

const handleVideoLoadedData = () => {
  isVideoReady.value = true
}

const addPlayHistory = (cardId) => {
  if (!cardId || historyRecordedIds.has(cardId)) {
    return
  }

  historyRecordedIds.add(cardId)
  addHistory({
    contentType: TPR_CONTENT_TYPE,
    contentId: cardId
  }).catch(() => {
    historyRecordedIds.delete(cardId)
  })
}

const handleVideoPause = () => {
  if (!isAutoMode.value) {
    isPlaying.value = false
  }
}

const handleVideoEnded = () => {
  if (!isAutoMode.value) {
    isPlaying.value = false
  }
}

const getList = async () => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    tprList.value = await getTprList(queryParams.value) || []
    currentIndex.value = 0
  } catch {
    tprList.value = []
    currentIndex.value = 0
  } finally {
    isLoading.value = false
  }
}

watch(
  () => [currentCard.value.id, currentCard.value.coverUrl, currentCard.value.videoUrl],
  ([cardId]) => {
    isVideoReady.value = false
    preloadCoverAspectRatio(currentCard.value)
    if(!isGuestUser.value) {
      loadFavoriteState(cardId)
    }
  },
  { immediate: true }
)

onMounted(async () => {
  startLearningUpdateTimer()
  window.addEventListener('keydown', handleKeydown)
  document.addEventListener('touchstart', handleTouchStart, { passive: true })
  document.addEventListener('touchend', handleTouchEnd, { passive: true })
  await getList()
  
  if (ensureCanUseTprFullscreen()) {
    setTimeout(() => {
      void startPlay()
    }, 500)
  }
})

onUnmounted(() => {
  stopLearningUpdateTimer()
  window.removeEventListener('keydown', handleKeydown)
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('touchend', handleTouchEnd)
  stopPlay()
})

return (_ctx: any,_cache: any) => {
  const _component_desktop_brand_corner = _resolveComponent("desktop-brand-corner")!
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["fullscreen-page", { 'clean-mode': isCleanMode.value, 'settings-open': showSettings.value }]),
    onClick: toggleCleanMode
  }, [
    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "bg-gradient" }, null, -1)),
    _cache[8] || (_cache[8] = _createElementVNode("div", { class: "orb orb-1" }, null, -1)),
    _cache[9] || (_cache[9] = _createElementVNode("div", { class: "orb orb-2" }, null, -1)),
    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "orb orb-3" }, null, -1)),
    _createVNode(_component_desktop_brand_corner),
    _createElementVNode("button", {
      type: "button",
      class: "back-btn",
      onClick: _withModifiers(handleGoBack, ["stop"]),
      "aria-label": "返回上一页"
    }, [
      _createVNode(_component_font_awesome_icon, { icon: "chevron-left" })
    ]),
    _createElementVNode("header", {
      class: _normalizeClass(["page-header", { hidden: !showTranslation.value }])
    }, [
      _createElementVNode("h1", {
        class: _normalizeClass(["word-translation", { visible: true }])
      }, _toDisplayString(currentCard.value.zh), 1)
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass(["side-controls", { hidden: isCleanMode.value }])
    }, [
      _createElementVNode("button", {
        class: _normalizeClass(["control-btn favorite-btn", { active: isCurrentCardFavorite.value }]),
        disabled: isCurrentFavoriteLoading.value,
        onClick: _withModifiers(toggleFavorite, ["stop"]),
        title: isCurrentCardFavorite.value ? '取消收藏' : '收藏'
      }, [
        _createVNode(_component_font_awesome_icon, { icon: "heart" })
      ], 10, _hoisted_1),
      _createElementVNode("button", {
        class: "control-btn",
        onClick: _withModifiers(openSettings, ["stop"]),
        title: "设置"
      }, [
        _createVNode(_component_font_awesome_icon, { icon: "cog" })
      ]),
      _createElementVNode("button", {
        class: _normalizeClass(["control-btn auto-play-btn", { active: isAutoMode.value }]),
        onClick: _withModifiers(startAutoPlay, ["stop"]),
        title: isAutoMode.value ? '自动播放中' : '开始自动播放'
      }, [
        _createVNode(_component_font_awesome_icon, {
          icon: isAutoMode.value ? 'book-open' : ['fas', 'hand-pointer']
        }, null, 8, ["icon"])
      ], 10, _hoisted_2)
    ], 2),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", {
        class: "video-container",
        style: _normalizeStyle({ '--tpr-video-aspect-ratio': currentVideoAspectRatio.value }),
        onClick: _withModifiers(togglePlay, ["stop"])
      }, [
        _createElementVNode("div", _hoisted_4, [
          (_openBlock(), _createElementBlock("video", {
            key: currentCard.value.id,
            ref_key: "fullscreenVideoRef",
            ref: fullscreenVideoRef,
            class: _normalizeClass(["fullscreen-video", { 'is-ready': isVideoReady.value }]),
            src: currentCard.value.videoUrl,
            poster: currentCard.value.coverUrl,
            playsinline: "",
            preload: "auto",
            controlslist: "nodownload nofullscreen noremoteplayback",
            disablepictureinpicture: "",
            onLoadedmetadata: handleVideoMetadata,
            onLoadeddata: handleVideoLoadedData,
            onPlay: handleVideoPlay,
            onPause: handleVideoPause,
            onEnded: handleVideoEnded
          }, null, 42, _hoisted_5)),
          (!isVideoReady.value && currentCard.value.coverUrl)
            ? (_openBlock(), _createElementBlock("img", {
                key: 0,
                class: "fullscreen-video-cover",
                src: currentCard.value.coverUrl,
                alt: ""
              }, null, 8, _hoisted_6))
            : _createCommentVNode("", true),
          (!isPlaying.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createVNode(_component_font_awesome_icon, {
                  icon: cards.value.length === 0 ? 'hand-paper' : 'play'
                }, null, 8, ["icon"])
              ]))
            : _createCommentVNode("", true)
        ])
      ], 4),
      _createElementVNode("h2", {
        class: _normalizeClass(["word-display", { visible: true }])
      }, _toDisplayString(currentCard.value.en), 1)
    ]),
    _createElementVNode("footer", {
      class: _normalizeClass(["bottom-controls", { hidden: isCleanMode.value }])
    }, [
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("button", {
          class: "play-btn",
          onClick: _withModifiers(togglePlay, ["stop"])
        }, [
          _createVNode(_component_font_awesome_icon, {
            icon: isPlaying.value ? 'pause' : 'play'
          }, null, 8, ["icon"])
        ])
      ])
    ], 2),
    _createElementVNode("div", _hoisted_9, [
      _createElementVNode("span", _hoisted_10, _toDisplayString(cards.value.length === 0 ? 0 : currentIndex.value + 1) + "/" + _toDisplayString(cards.value.length), 1)
    ]),
    _createElementVNode("button", {
      class: "nav-btn prev",
      onClick: _withModifiers(prevCard, ["stop"])
    }, [
      _createVNode(_component_font_awesome_icon, { icon: "chevron-left" })
    ]),
    _createElementVNode("button", {
      class: "nav-btn next",
      onClick: _withModifiers(nextCard, ["stop"])
    }, [
      _createVNode(_component_font_awesome_icon, { icon: "chevron-right" })
    ]),
    _createElementVNode("div", _hoisted_11, null, 512),
    (showSettings.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "settings-modal",
          onClick: _withModifiers(closeSettings, ["stop"])
        }, [
          _createElementVNode("div", {
            class: "settings-content",
            onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", _hoisted_13, [
                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "settings-text" }, [
                  _createElementVNode("p", { class: "settings-title" }, "显示翻译"),
                  _createElementVNode("p", { class: "settings-desc" }, "翻译显示在绘本顶部")
                ], -1)),
                _createElementVNode("label", _hoisted_14, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showTranslation).value = $event))
                  }, null, 512), [
                    [_vModelCheckbox, showTranslation.value]
                  ]),
                  _cache[3] || (_cache[3] = _createElementVNode("span", { class: "slider" }, null, -1))
                ])
              ]),
              _createElementVNode("div", _hoisted_15, [
                _cache[6] || (_cache[6] = _createElementVNode("div", { class: "settings-text" }, [
                  _createElementVNode("p", { class: "settings-title" }, "手动翻页时音频自动播放")
                ], -1)),
                _createElementVNode("label", _hoisted_16, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((manualFlipAutoPlay).value = $event))
                  }, null, 512), [
                    [_vModelCheckbox, manualFlipAutoPlay.value]
                  ]),
                  _cache[5] || (_cache[5] = _createElementVNode("span", { class: "slider" }, null, -1))
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})